import { storage, sessionStorageManager } from '../utils/storage.utils'

export class StorageService {
  private prefix: string

  constructor(prefix: string = 'templatean') {
    this.prefix = prefix
  }

  private getKey(key: string): string {
    return `${this.prefix}_${key}`
  }

  // Local storage methods
  async get<T>(key: string): Promise<T | null> {
    return storage.get<T>(this.getKey(key))
  }

  async set<T>(key: string, value: T): Promise<void> {
    return storage.set(this.getKey(key), value)
  }

  async remove(key: string): Promise<void> {
    return storage.remove(this.getKey(key))
  }

  async clear(): Promise<void> {
    const keys = Object.keys(localStorage)
    const prefixedKeys = keys.filter(key => key.startsWith(this.prefix))

    for (const key of prefixedKeys) {
      localStorage.removeItem(key)
    }
  }

  // Session storage methods
  async getSession<T>(key: string): Promise<T | null> {
    return sessionStorageManager.get<T>(this.getKey(key))
  }

  async setSession<T>(key: string, value: T): Promise<void> {
    return sessionStorageManager.set(this.getKey(key), value)
  }

  async removeSession(key: string): Promise<void> {
    return sessionStorageManager.remove(this.getKey(key))
  }

  async clearSession(): Promise<void> {
    const keys = Object.keys(window.sessionStorage)
    const prefixedKeys = keys.filter(key => key.startsWith(this.prefix))

    for (const key of prefixedKeys) {
      window.sessionStorage.removeItem(key)
    }
  }

  // Specific storage methods
  async saveRecentItems<T>(category: string, items: T[], maxItems: number = 10): Promise<void> {
    const key = `recent_${category}`
    const limitedItems = items.slice(0, maxItems)
    await this.set(key, limitedItems)
  }

  async getRecentItems<T>(category: string): Promise<T[]> {
    const key = `recent_${category}`
    const items = await this.get<T[]>(key)
    return items || []
  }

  async addRecentItem<T>(category: string, item: T, maxItems: number = 10): Promise<void> {
    const items = await this.getRecentItems<T>(category)
    const filtered = items.filter((i: any) => {
      if (typeof i === 'object' && i !== null && 'id' in i && 'id' in (item as any)) {
        return i.id !== (item as any).id
      }
      return i !== item
    })
    const updated = [item, ...filtered].slice(0, maxItems)
    await this.saveRecentItems(category, updated, maxItems)
  }

  async saveDraft<T>(category: string, id: string, draft: T): Promise<void> {
    const key = `draft_${category}_${id}`
    await this.set(key, {
      data: draft,
      timestamp: new Date().toISOString(),
    })
  }

  async getDraft<T>(category: string, id: string): Promise<{ data: T; timestamp: string } | null> {
    const key = `draft_${category}_${id}`
    return this.get<{ data: T; timestamp: string }>(key)
  }

  async removeDraft(category: string, id: string): Promise<void> {
    const key = `draft_${category}_${id}`
    await this.remove(key)
  }

  async saveUserPreferences(preferences: Record<string, any>): Promise<void> {
    await this.set('user_preferences', preferences)
  }

  async getUserPreferences(): Promise<Record<string, any>> {
    const prefs = await this.get<Record<string, any>>('user_preferences')
    return prefs || {}
  }

  async updateUserPreference(key: string, value: any): Promise<void> {
    const prefs = await this.getUserPreferences()
    prefs[key] = value
    await this.saveUserPreferences(prefs)
  }

  // Cache with expiration
  async setWithExpiry<T>(key: string, value: T, expiryMinutes: number): Promise<void> {
    const item = {
      value,
      expiry: new Date().getTime() + (expiryMinutes * 60 * 1000),
    }
    await this.set(key, item)
  }

  async getWithExpiry<T>(key: string): Promise<T | null> {
    const item = await this.get<{ value: T; expiry: number }>(key)
    if (!item) return null

    if (new Date().getTime() > item.expiry) {
      await this.remove(key)
      return null
    }

    return item.value
  }
}

export const storageService = new StorageService()
